import React, { useState, useEffect } from 'react';
import { STITCH_SERVICES } from '../data/stitchData';

export default function PracticePortfolioCarousel({ onOpenService, activeServiceSelection }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const total = STITCH_SERVICES.length;

  useEffect(() => {
    if (activeServiceSelection?.id) {
      const idx = STITCH_SERVICES.findIndex((s) => s.id === activeServiceSelection.id);
      if (idx !== -1) {
        setCurrentIndex(idx);
      }
    }
  }, [activeServiceSelection]);

  const currentService = STITCH_SERVICES[currentIndex];

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + total) % total);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % total);
  };

  // Secondary items are the next 3 items circularly
  const secondaryIndices = [
    (currentIndex + 1) % total,
    (currentIndex + 2) % total,
    (currentIndex + 3) % total
  ];

  return (
    <section id="services" className="relative z-10 w-full max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 pb-16">
      {/* Header and Controls */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pb-6">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="text-[11px] uppercase tracking-widest text-primary dark:text-primary-fixed font-bold">
              Clinical Advisory Suite
            </span>
            <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-surface-container-high dark:bg-clinical-slate-800 text-gray-700 dark:text-slate-200">
              {currentIndex + 1} of {total} Active
            </span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white tracking-tight">
            Interactive Practice Portfolio
          </h2>
        </div>

        {/* Arrow Navigation */}
        <div className="flex items-center gap-2 self-end sm:self-auto">
          <button
            type="button"
            onClick={handlePrev}
            aria-label="Previous service"
            className="w-11 h-11 rounded-full bg-white/95 dark:bg-clinical-slate-800/90 backdrop-blur-xl border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-primary hover:text-white shadow-sm transition-all duration-300 flex items-center justify-center active:scale-95"
          >
            <span className="material-symbols-outlined text-[22px]">arrow_back</span>
          </button>
          <button
            type="button"
            onClick={handleNext}
            aria-label="Next service"
            className="w-11 h-11 rounded-full bg-white/95 dark:bg-clinical-slate-800/90 backdrop-blur-xl border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-primary hover:text-white shadow-sm transition-all duration-300 flex items-center justify-center active:scale-95"
          >
            <span className="material-symbols-outlined text-[22px]">arrow_forward</span>
          </button>
        </div>
      </div>

      {/* 4-Card Hierarchical Viewport */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch w-full min-h-[500px]">
        {/* Primary Card (7 cols) */}
        <div
          onClick={() => onOpenService(currentService)}
          className="lg:col-span-7 relative group rounded-[32px] overflow-hidden bg-gray-900 border border-glass-border-light dark:border-gray-800 shadow-[0_24px_50px_-12px_rgba(0,97,148,0.16)] flex flex-col justify-between transition-all duration-500 cursor-pointer min-h-[440px] lg:min-h-[520px]"
        >
          {/* Background Image */}
          <div
            className="absolute inset-0 bg-cover bg-center transition-transform duration-700 ease-out group-hover:scale-105"
            style={{ backgroundImage: `url('${currentService.heroImg}')` }}
          ></div>
          <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-gray-950/65 to-gray-950/20"></div>

          {/* Top Row: Category Badge & Inspect Icon */}
          <div className="relative z-10 p-6 sm:p-8 flex items-center justify-between gap-2">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/20 backdrop-blur-md border border-white/30 text-white">
              <span className="material-symbols-outlined text-ice-cyan text-[18px]">
                {currentService.icon}
              </span>
              <span className="text-[11px] uppercase tracking-wider font-bold text-ice-cyan">
                {currentService.category}
              </span>
            </div>

            <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-md text-white flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-all duration-300">
              <span className="material-symbols-outlined text-[20px]">open_in_new</span>
            </div>
          </div>

          {/* Bottom Content Area */}
          <div className="relative z-10 p-6 sm:p-8 space-y-4 text-white">
            <div className="space-y-1">
              <h3 className="text-2xl sm:text-3xl font-bold tracking-tight text-white group-hover:text-ice-cyan transition-colors">
                {currentService.title}
              </h3>
              <p className="text-sm sm:text-base text-gray-200 max-w-2xl leading-relaxed">
                {currentService.subtitle}
              </p>
            </div>

            <div className="pt-2 flex flex-wrap items-center justify-between gap-4 border-t border-white/20">
              <div className="flex items-center gap-2 text-ice-cyan text-xs font-medium">
                <span className="material-symbols-outlined text-[18px]">verified</span>
                <span>{currentService.audience}</span>
              </div>

              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenService(currentService);
                }}
                className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-primary hover:bg-primary-container text-white text-xs font-semibold shadow-md transition-all active:scale-95"
              >
                <span>Explore Full Protocol</span>
                <span className="material-symbols-outlined text-[16px]">arrow_forward</span>
              </button>
            </div>
          </div>
        </div>

        {/* Secondary Cards Column (5 cols) */}
        <div className="lg:col-span-5 flex flex-col justify-between h-full w-full gap-4">
          {secondaryIndices.map((idx, slotIdx) => {
            const secondaryItem = STITCH_SERVICES[idx];
            return (
              <div
                key={slotIdx}
                onClick={() => setCurrentIndex(idx)}
                className="flex-1 group relative rounded-2xl bg-white/95 dark:bg-clinical-slate-900/90 border border-gray-200 dark:border-gray-800 shadow-sm hover:shadow-md hover:border-primary/50 transition-all duration-300 cursor-pointer flex items-center justify-between gap-4 p-4 w-full"
              >
                <div className="flex items-center gap-4 min-w-0 flex-1">
                  <div className="relative w-24 h-24 rounded-2xl overflow-hidden shrink-0 bg-gray-100 dark:bg-clinical-slate-800 shadow-xs">
                    <img
                      src={secondaryItem.heroImg}
                      alt={secondaryItem.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-primary/10"></div>
                  </div>

                  <div className="min-w-0 flex-1 flex flex-col justify-center space-y-1">
                    <span className="text-[11px] uppercase tracking-wider text-primary dark:text-primary-fixed font-bold truncate">
                      {secondaryItem.category}
                    </span>
                    <h4 className="text-base font-bold text-gray-900 dark:text-white line-clamp-1 leading-snug group-hover:text-primary transition-colors">
                      {secondaryItem.title}
                    </h4>
                    <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-slate-400">
                      <span className="material-symbols-outlined text-primary text-[16px] shrink-0">
                        trending_up
                      </span>
                      <span className="font-medium truncate">{secondaryItem.statBadge}</span>
                    </div>
                  </div>
                </div>

                <div className="shrink-0 w-9 h-9 rounded-full bg-surface-container dark:bg-clinical-slate-800 flex items-center justify-center text-gray-400 dark:text-gray-300 group-hover:bg-primary group-hover:text-white transition-all shadow-xs">
                  <span className="material-symbols-outlined text-[18px]">arrow_upward</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Progress Bar & Dots Track */}
      <div className="pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
        {/* 8 Dots Track */}
        <div className="flex items-center gap-2">
          {STITCH_SERVICES.map((_, dotIdx) => (
            <button
              key={dotIdx}
              type="button"
              onClick={() => setCurrentIndex(dotIdx)}
              aria-label={`Go to service ${dotIdx + 1}`}
              className={`h-2.5 rounded-full transition-all duration-300 ${
                dotIdx === currentIndex
                  ? 'w-8 bg-primary'
                  : 'w-2.5 bg-gray-300 dark:bg-gray-700 hover:bg-gray-400'
              }`}
            ></button>
          ))}
        </div>

        {/* Direct Fast Jump Chips */}
        <div className="flex flex-wrap items-center justify-center gap-1.5 text-xs font-medium text-gray-600 dark:text-slate-300">
          <span className="text-gray-400 dark:text-gray-500 uppercase tracking-wider text-[10px] mr-1">
            Direct Focus:
          </span>
          {STITCH_SERVICES.map((item, fIdx) => (
            <button
              key={fIdx}
              type="button"
              onClick={() => setCurrentIndex(fIdx)}
              className={`px-2.5 py-1 rounded-full text-[11px] transition-all ${
                fIdx === currentIndex
                  ? 'bg-primary text-white font-semibold shadow-xs'
                  : 'bg-surface-container-low dark:bg-clinical-slate-800 text-gray-600 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-clinical-slate-700'
              }`}
            >
              {item.title.split(' ')[0]} {item.title.split(' ')[1] || ''}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
